import React, { useState, useEffect, useRef } from "react";
import { Trash2, SquarePen, Eye, Plus } from "lucide-react";
import { toast } from "react-toastify";
import ProgressBar from "../../../utils/progressBar/ProgressBar";
import { Table, Thead, Tbody, Tr, Th, Td, TableContainer, Checkbox, Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, Button, Spinner, ModalCloseButton } from "@chakra-ui/react";
import "./Documents.css";
import EllipsisMenu from "../../../utils/EllipsisDropdown/EllipsisMenu";
import { formatDate } from "../../../utils/commonFunction/CommonFunction";
import DocumentsForm from "./DocumentsForm";
import DocumentsDetailView from "./DocumentsDetailView";
import { useNavigate } from "react-router-dom";

function DocumentsList(sidebarColor) {
  const desktopRef = useRef(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showProgressBar, setShowProgressBar] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [dataToEdit, setDataToEdit] = useState({});
  const [documentsROWId, setDocumentsROWId] = useState("");
  const [documentsId, setDocumentsId] = useState("");
  // Remove modal detail open state and selected document local state
  // Instead use a selectedDocument state to display details in overview tab
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [activeDetailTab, setActiveDetailTab] = useState(false);

  // Helper function to strip HTML tags
  const stripHtml = (html) => {
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  // Mock data for documents
  const [documentsData, setDocumentsData] = useState([
    {
      documents: {
        ROWID: 1,
        title: "Sample Document 1",
        createdDate: "2023-10-01",
        aadhaarCard: "1234-5678-9012",
        photo: "Photo uploaded successfully",
        registry: "Registry details for property",
        electricityBill: "Electricity bill for address XYZ",
        cancelCheque: "Cancel cheque from ABC Bank",
        mobileNo: "9876543210",
        emailId: "user1@example.com",
        document: "<p>Document content for sample 1</p>",
        guidelines: "<p>Guidelines content for sample 1</p>",
        note: "<p>Note content for sample 1</p>",
        contactDetails: "<p>Contact details for department A</p>",
        bankDetail: "<p>Bank detail content for sample 1</p>"
      }
    },
    {
      documents: {
        ROWID: 2,
        title: "Sample Document 2",
        createdDate: "2023-10-05",
        aadhaarCard: "2345-6789-0123",
        photo: "Photo uploaded successfully",
        registry: "House tax agreement details",
        electricityBill: "Electricity bill information",
        cancelCheque: "Bank passbook details",
        mobileNo: "8765432109",
        emailId: "user2@example.com",
        document: "<p>Another document content for sample 2</p>",
        guidelines: "<p>Additional guidelines for sample 2</p>",
        note: "<p>Some notes here for sample 2</p>",
        contactDetails: "<p>More contact info for department B</p>",
        bankDetail: "<p>Bank details for doc 2</p>"
      }
    },
    {
      documents: {
        ROWID: 3,
        title: "Sample Document 3",
        createdDate: "2023-10-10",
        aadhaarCard: "3456-7890-1234",
        photo: "Photo uploaded successfully",
        registry: "Full payment agreement details",
        electricityBill: "Electricity bill for third document",
        cancelCheque: "Cancel cheque for third",
        mobileNo: "7654321098",
        emailId: "user3@example.com",
        document: "<p>Third document content</p>",
        guidelines: "<p>Guidelines for third document</p>",
        note: "<p>Notes for third document</p>",
        contactDetails: "<p>Contact for third department</p>",
        bankDetail: "<p>Bank for third document</p>"
      }
    },
    {
      documents: {
        ROWID: 4,
        title: "John Doe Application",
        createdDate: "2023-10-15",
        aadhaarCard: "4567-8901-2345",
        photo: "Passport size photo uploaded",
        registry: "House registry and tax payment agreement for 123 Main St",
        electricityBill: "Electricity bill for residential address",
        cancelCheque: "Cancel cheque from HDFC Bank account",
        mobileNo: "9123456789",
        emailId: "johndoe@example.com",
        document: "<p>Application document for John Doe</p>",
        guidelines: "<p>Follow all submission guidelines carefully</p>",
        note: "<p>Additional notes: Ensure all documents are original</p>",
        contactDetails: "<p>Contact: Finance Department, Phone: 9876543210</p>",
        bankDetail: "<p>Bank: HDFC, Account: 1234567890</p>",
        items: [
          { document: "Aadhaar Card" },
          { document: "Photo" },
          { document: "Registry/House Tax/Full Payment Agreement" },
          { document: "Electricity Bill" },
          { document: "Cancel Cheque/Bank Passbook" }
        ]
      }
    }
  ]);

  // Mock permissions
  const permissions = localStorage.getItem("permissions");
  const filterData = JSON.parse(permissions) || [];
const navigate = useNavigate();
  // Handle row deletion
  const handleDelete = () => {
    setShowProgressBar(true);
    setLoading(true);
    const idsToDelete = selectedRows.length === 0 ? [documentsROWId] : selectedRows;
    // Mock delete
    setDocumentsData(prev => prev.filter(item => !idsToDelete.includes(item.documents.ROWID)));
    setSelectedRows([]);
    toast.success("Document deleted successfully");
    setIsOpen(false);
    setDocumentsROWId("");
    setShowProgressBar(false);
    setLoading(false);
  };

  // Modal for delete confirmation
  const openTrash = (ROWID) => {
    setDocumentsROWId(ROWID);
    setIsOpen(true);
  };

  const closeTrash = () => {
    setIsOpen(false);
    setDocumentsROWId("");
  };

  // Handle row selection
  const handleRowSelect = (ROWID) => {
    setSelectedRows((prevSelectedRows) => {
      const isSelected = prevSelectedRows.includes(ROWID);
      const updatedRows = isSelected ? prevSelectedRows.filter((id) => id !== ROWID) : [...prevSelectedRows, ROWID];
      return updatedRows;
    });
  };

  // Handle "select all" functionality
  const handleSelectAllRows = () => {
    setSelectedRows((prevSelectedRows) => {
      const updatedRows = prevSelectedRows.length === documentsData?.length ? [] : documentsData.map((item) => item?.documents?.ROWID);
      return updatedRows;
    });
  };

  const openDrawer = () => {
    setIsDrawerOpen(true);
  };

  const closeDrawer = () => {
    setIsDrawerOpen(false);
  };

  const onNewDataAdded = () => {
    // Mock refresh
  };

  return (
    <>
      {loading && (
        <div className="spinner-overlay">
          <Spinner size="xl" color="pink.500" />
        </div>
      )}
      {showProgressBar && <ProgressBar colorScheme="pink" />}
      <div className="d-flex">
        <div className="list-view-container" style={{ width: selectedDocument ? "50%" : "100%" }}>
          <div ref={desktopRef}>
            <div className="d-flex justify-content-end">
              <button className="new-button mx-2" onClick={openDrawer}>
                <Plus size="18px" />
                <span className="ms-1">New</span>
              </button>
            </div>
            {selectedRows.length > 0 && (
              <>
                {filterData?.find((mod) => mod.moduleName === "Documents" && mod.delete === true) && (
                  <button className="new-button mx-2" onClick={openTrash}>
                    <Plus size="18px" />
                    <span className="ms-1">Delete All</span>
                  </button>
                )}
              </>
            )}
            <div className="custom-scroll" style={{ minHeight: "75vh", overflow: "scroll" }}>
              <TableContainer
                className="card card-detail mt-1 scrollable-container"
                style={{ minHeight: "75vh", backgroundColor: "white", border: "none", position: "relative", overflowY: "scroll", scrollbarColor: "#A0AEC0 #EDF2F7", scrollbarWidth: "10px" }}
              >
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr className="tr-border">
                      <Th className="py-1 th-color">
                        <Checkbox isChecked={selectedRows.length === documentsData?.length} onChange={handleSelectAllRows} />
                      </Th>
                      <Th className="py-1 th-color">Actions</Th>
                      <Th className="py-1 th-color">Document</Th>
                      <Th className="py-1 th-color">Guidelines</Th>
                      <Th className="py-1 th-color">Note</Th>
                      <Th className="py-1 th-color">Contact Detail</Th>
                      <Th className="py-1 th-color">Bank Detail</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {documentsData &&
                      documentsData?.map((item, index) => (
                        <Tr key={index} className="tr-border" style={{ cursor: "pointer", position: "relative" }}>
                          <Td>
                            <Checkbox isChecked={selectedRows.includes(item?.documents?.ROWID)} onChange={() => handleRowSelect(item?.documents?.ROWID)} />
                          </Td>
                          <Td style={{ width: "110px", textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
                            <EllipsisMenu
                              item={item}
                              onView={() => {
                                navigate("/condition-view", { state: { document: item.documents } });
                              }}
                              onEdit={() => {
                                openDrawer(item.documents);
                                setDataToEdit(item.documents);
                                setIsUpdateMode(true);
                                setDocumentsId(item?.documents?.ROWID);
                              }}
                              onDelete={() => {
                                openTrash();
                                setDocumentsROWId(item?.documents?.ROWID);
                              }}
                              permissions={{
                                canEdit: filterData?.find((mod) => mod.moduleName === "Documents" && mod.modifyAll === true),
                                canDelete: filterData?.find((mod) => mod.moduleName === "Documents" && mod.delete === true),
                                canView: true,
                              }}
                            />
                          </Td>
                          <Td className="font-colors"> {item?.documents?.title} </Td>
                          <Td className="font-colors">{stripHtml(item?.documents?.guidelines)}</Td>
                          <Td className="font-colors">{stripHtml(item?.documents?.note)}</Td>
                          <Td className="font-colors">{stripHtml(item?.documents?.contactDetails)}</Td>
                          <Td className="font-colors">{stripHtml(item?.documents?.bankDetail)}</Td>
                        </Tr>
                      ))}
                  </Tbody>
                </Table>
              </TableContainer>
            </div>
          </div>
        </div>
        
      </div>
      <DocumentsForm
        closeDrawer={closeDrawer}
        isDrawerOpen={isDrawerOpen}
        isUpdateMode={isUpdateMode}
        documentsId={documentsId}
        setIsUpdateMode={setIsUpdateMode}
        dataToEdit={dataToEdit}
        onNewDataAdded={onNewDataAdded}
      />
    </>
  );
}

export default DocumentsList;
